/**
 * @fileoverview Composable for managing visible app icons
 * @module composables/useVisibleApps
 *
 * Extracts the logic for filtering, sorting, and displaying app icons
 * based on their enabled status and position.
 */
import { computed, type Ref, type ComputedRef } from 'vue'
import * as _ from 'lodash-es'

/**
 * Composable for managing visible app icons
 *
 * @param showIcons - Reactive ref controlling icon visibility
 * @param settings - Reactive computed ref containing app settings
 * @returns Computed property with filtered and sorted visible apps
 */
export function useVisibleApps(
  showIcons: Ref<boolean>,
  settings: ComputedRef<any>
) {
  /**
   * Get list of visible app icons to display
   * Filters enabled apps, assigns IDs, and sorts by position
   */
  const visibleApps = computed((): any[] | Record<string, never> => {
    if (showIcons.value && settings.value.apps) {
      const mappedVisibleApps = _.map(settings.value.apps, (app: any, id: string) => {
        if (app && app.enabled) {
          app.id = id
          return app
        } else {
          return null
        }
      })
      const compactVisibleApps = _.compact(mappedVisibleApps)
      const sortedVisibleAppsByPosition = _.sortBy(compactVisibleApps, 'position')
      return sortedVisibleAppsByPosition
    } else {
      return {}
    }
  })

  /**
   * Count of enabled apps
   */
  const numberOfApps = computed(() =>
    settings.value.apps ? _.filter(settings.value.apps, (app: any) => app && app.enabled).length : 0
  )

  return {
    visibleApps,
    numberOfApps,
  }
}
